import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { toast } from 'sonner';

import {
  type Personalization,
  personalizationAttributes,
} from '@backend/orders/orders.personalization';

import {
  addToCart,
  createCart,
  getCart,
  removeFromCart,
  updateCart,
} from '@frontend/storefront/lib/shopify/cart';
import type {
  Cart,
  CartItem,
  Product,
  ProductVariant,
} from '@frontend/storefront/lib/shopify/types';

type UpdateType = 'plus' | 'minus' | 'delete';

type CartAction =
  | {
      type: 'UPDATE_ITEM';
      payload: { merchandiseId: string; updateType: UpdateType };
    }
  | {
      type: 'ADD_ITEM';
      payload: {
        variant: ProductVariant;
        product: Product;
        attributes: { key: string; value: string }[];
        lineId: string;
      };
    };

type CartContextType = {
  cart: Cart | undefined;
  updateCartItem: (merchandiseId: string, updateType: UpdateType) => void;
  addCartItem: (
    variant: ProductVariant,
    product: Product,
    info: Personalization,
  ) => void;
  isUpdating: boolean;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

function calculateItemCost(quantity: number, price: string): string {
  return (Number(price) * quantity).toString();
}

function updateCartItem(
  item: CartItem,
  updateType: UpdateType,
): CartItem | null {
  if (updateType === 'delete') return null;

  const newQuantity =
    updateType === 'plus' ? item.quantity + 1 : item.quantity - 1;
  if (newQuantity === 0) return null;

  const singleItemAmount = Number(item.cost.totalAmount.amount) / item.quantity;
  const newTotalAmount = calculateItemCost(
    newQuantity,
    singleItemAmount.toString(),
  );

  return {
    ...item,
    quantity: newQuantity,
    cost: {
      ...item.cost,
      totalAmount: {
        ...item.cost.totalAmount,
        amount: newTotalAmount,
      },
    },
  };
}

function createOrUpdateCartItem(
  existingItem: CartItem | undefined,
  variant: ProductVariant,
  product: Product,
): CartItem {
  const quantity = existingItem ? existingItem.quantity + 1 : 1;
  const totalAmount = calculateItemCost(quantity, variant.price.amount);

  return {
    id: existingItem?.id,
    quantity,
    cost: {
      totalAmount: {
        amount: totalAmount,
        currencyCode: variant.price.currencyCode,
      },
    },
    merchandise: {
      id: variant.id,
      title: variant.title,
      selectedOptions: variant.selectedOptions,
      product: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        featuredImage: product.featuredImage,
      },
    },
  };
}

function updateCartTotals(
  lines: CartItem[],
): Pick<Cart, 'totalQuantity' | 'cost'> {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce(
    (sum, item) => sum + Number(item.cost.totalAmount.amount),
    0,
  );
  const currencyCode = lines[0]?.cost.totalAmount.currencyCode ?? 'USD';

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode },
    },
  };
}

function createEmptyCart(): Cart {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'USD' },
      totalAmount: { amount: '0', currencyCode: 'USD' },
      totalTaxAmount: { amount: '0', currencyCode: 'USD' },
    },
  };
}

function cartReducer(state: Cart | undefined, action: CartAction): Cart {
  const currentCart = state || createEmptyCart();

  switch (action.type) {
    case 'UPDATE_ITEM': {
      const { merchandiseId, updateType } = action.payload;
      const updatedLines = currentCart.lines
        .map((item) =>
          item.id === merchandiseId ? updateCartItem(item, updateType) : item,
        )
        .filter(Boolean) as CartItem[];

      if (updatedLines.length === 0) {
        return {
          ...currentCart,
          lines: [],
          totalQuantity: 0,
          cost: {
            ...currentCart.cost,
            totalAmount: { ...currentCart.cost.totalAmount, amount: '0' },
          },
        };
      }

      return {
        ...currentCart,
        ...updateCartTotals(updatedLines),
        lines: updatedLines,
      };
    }
    case 'ADD_ITEM': {
      const { variant, product, attributes, lineId } = action.payload;
      const updatedItem = {
        ...createOrUpdateCartItem(undefined, variant, product),
        id: lineId,
        attributes,
      };
      const updatedLines = [...currentCart.lines, updatedItem];

      return {
        ...currentCart,
        ...updateCartTotals(updatedLines),
        lines: updatedLines,
      };
    }
    default:
      return currentCart;
  }
}

// The cart lives in Shopify; the browser only remembers its ID.
const CART_ID_KEY = 'nopad.cartId';

function readCartId(): string | null {
  try {
    return localStorage.getItem(CART_ID_KEY);
  } catch {
    return null;
  }
}

function writeCartId(cartId: string | null) {
  try {
    if (cartId) localStorage.setItem(CART_ID_KEY, cartId);
    else localStorage.removeItem(CART_ID_KEY);
  } catch {
    // Storage blocked (private mode); the cart lasts for this page view.
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [isUpdating, setUpdating] = useState(false);
  const busy = useRef(false);
  const [cart, setCart] = useState<Cart | undefined>(undefined);
  const cartRef = useRef(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    const cartId = readCartId();
    if (!cartId) return;

    getCart(cartId)
      .then((existing) => {
        // A cart that went through checkout comes back empty.
        if (existing) setCart(existing);
        else writeCartId(null);
      })
      .catch(() => writeCartId(null));
  }, []);

  const ensureCartId = useCallback(async () => {
    const existing = cartRef.current?.id ?? readCartId();
    if (existing) return existing;

    const created = await createCart();
    writeCartId(created.id!);
    return created.id!;
  }, []);

  // Apply the change locally right away, then confirm it with Shopify.
  const sync = useCallback(
    async (action: CartAction, request: (cartId: string) => Promise<Cart>) => {
      if (busy.current) return;
      busy.current = true;
      setUpdating(true);
      const previous = cartRef.current;
      setCart((current) => cartReducer(current, action));

      try {
        const cartId = await ensureCartId();
        setCart(await request(cartId));
      } catch {
        setCart(previous);
        toast.error("Couldn't update your cart. Please try again.");
      } finally {
        busy.current = false;
        setUpdating(false);
      }
    },
    [ensureCartId],
  );

  const addCartItem = useCallback(
    (variant: ProductVariant, product: Product, info: Personalization) => {
      const lineId = crypto.randomUUID();
      const attributes = [
        ...personalizationAttributes(info),
        { key: '_nopad_book', value: lineId },
      ];
      sync(
        { type: 'ADD_ITEM', payload: { variant, product, attributes, lineId } },
        (cartId) =>
          addToCart(cartId, [
            { merchandiseId: variant.id, quantity: 1, attributes },
          ]),
      );
    },
    [sync],
  );

  const updateCartItem = useCallback(
    (merchandiseId: string, updateType: UpdateType) => {
      const line = cartRef.current?.lines.find(
        (item) => item.id === merchandiseId,
      );
      // A line added a moment ago has no Shopify ID until Shopify answers.
      if (!line?.id) return;
      const lineId = line.id;

      const quantity =
        updateType === 'delete'
          ? 0
          : line.quantity + (updateType === 'plus' ? 1 : -1);

      sync(
        { type: 'UPDATE_ITEM', payload: { merchandiseId, updateType } },
        (cartId) =>
          quantity === 0
            ? removeFromCart(cartId, [lineId])
            : updateCart(cartId, [
                { id: lineId, merchandiseId: line.merchandise.id, quantity },
              ]),
      );
    },
    [sync],
  );

  const value = useMemo(
    () => ({ cart, updateCartItem, addCartItem, isUpdating }),
    [cart, updateCartItem, addCartItem, isUpdating],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}
